// ═══════════════════════════════════════════════════════════════════
// sculptedBeings.jsx — TERROR TOONZ: the Escendants' sculpted bodies
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-16: "when I say Let's update Terror Toonz I am talking
// about updating every single character model … the fail safe makes it
// all possible, worst case we just stick with what we got."
//
// ONE DOOR for every Escendant body in Terror. The Shatterfield
// (fieldBeings3D) and the companion squares (companionBody3D) render
// <BeingBody lineage tier onSkin/> instead of the kit's <BeingMesh/>.
//   · a lineage|tier with a sculpt → the sculpted glb, with the painted
//     kit body behind it (THE FAIL-SAFE LAW, sculptGate.jsx): shown while
//     the file loads, if it fails, and for the rest of the visit once the
//     frame rate can't keep up.
//   · everything else → the painted kit body, exactly as before.
//
// ⭐ THEY OWN THE BODY, WE OWN THE POSE. The glb is the master body
//   (gamemaster/models/sculpt/glb/, copied to public/models/). The walk,
//   the idle and the look-around live HERE, per sculpt, never in the file.
//   The Shatterfield's strike / kick / wound poses reach the sculpt the
//   same way they reach the painted body: through the rig door. The
//   sculpt's bones are renamed rig_head / rig_armL / … once, when it
//   mounts, so fieldBeings3D's findRig() finds them with no new code.
//
// `onSkin(k, id)` tells the host WHICH body is standing, so it can
//   re-measure its fit (the two bodies do not share a bounding box):
//   k = "s" when the sculpt mounts, "p" when the painted body mounts,
//   id = "lineage|tier" of the body that said it (a card that ascends in
//   place must never have the old body's word filed under the new one).
//   Both fire from LAYOUT effects, so the host re-fits before paint.
//
// `ctl` (optional, mutable, read per frame): { speed } — >0 walks.
//
// ⚠ useLoader fetches when a sculpt MOUNTS: only mount a BeingBody where
//   the body is actually shown (it is — the field and the companion box).
// PERF LAW: no React state from a frame; onSkin fires once per swap.
// ═══════════════════════════════════════════════════════════════════
import { useLayoutEffect, useRef } from "react";
import * as THREE from "three";
import { BeingMesh } from "./ieBeingModels";
import { makeSculptedBody } from "./sculptedBody";
import { failSafe } from "./sculptGate";

const MODEL_URL = (f) => `${process.env.PUBLIC_URL || ""}/models/${f}`;

// ── THE RIG DOOR, for sculpts ─────────────────────────────────────
// kit slot → the sculpt's bone. A quadruped reaches with its forelegs
// (the painted tiger binds them to the arm slots too).
const QUAD_DOOR = { head: "head", torso: "spine", armL: "foreUL", armR: "foreUR", legL: "thighL", legR: "thighR", tail: "tail0" };

// ── THE SPECTRAL RIM — the Phantom in a Phantom being ─────────────
// a faint fresnel (colour per being) on its coat and face meshes;
// chained after the loader's own vertex-colour patch, with its own
// program cache key.
const rimTweak = (color, amt, which = /coat|face/) => (o) => {
  if (!which.test(o.name)) return;
  const m = o.material;
  const prev = m.onBeforeCompile;
  const rgb = `vec3(${color.map((c) => c.toFixed(3)).join(",")})`;
  m.onBeforeCompile = (sh, r) => {
    if (prev) prev(sh, r);
    sh.fragmentShader = sh.fragmentShader.replace(
      "vec3 totalEmissiveRadiance = emissive * vColor.rgb;",
      `vec3 totalEmissiveRadiance = emissive * vColor.rgb; totalEmissiveRadiance += ${rgb} * pow(1.0 - abs(dot(normalize(vNormal), normalize(vViewPosition))), 4.0) * ${amt.toFixed(3)};`
    );
  };
  m.customProgramCacheKey = () => `ie-sculpt-rim-${rgb}-${amt}`;
};

// ── SPECTRAL TIGER BEB — the pose (ported from its builder's walkpose.js) ──
// A kitten's trot: diagonal pairs, a springy spine, the head held level,
// a slow question-mark tail, ears that flick. Bone axes are world axes at
// rest; +x swings a leg BACK.
function tigerKittenPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const breathe = Math.sin(t * 2.1) * 0.012 * (1 - amt);
  const bob = Math.sin(ph * 2) * 0.03 * amt;
  set("hips", 0.02 * amt + breathe, Math.sin(ph) * 0.05 * amt, Math.sin(ph) * 0.03 * amt);
  set("spine", -bob * 0.5, -Math.sin(ph) * 0.03 * amt, 0);
  set("chest", bob * 0.5 - breathe, -Math.sin(ph) * 0.04 * amt, -Math.sin(ph) * 0.03 * amt);
  set("neck", -bob * 0.6, Math.sin(ph) * 0.05 * amt, 0);
  set("head", 0.02 + Math.sin(t * 0.7) * 0.03 * (1 - amt), Math.sin(t * 0.45) * 0.28 * (1 - amt) + Math.sin(ph) * 0.03 * amt, Math.sin(t * 0.33) * 0.08 * (1 - amt));
  const flick = (o) => Math.max(0, Math.sin(t * 1.3 + o) - 0.93) * 6;
  set("earL", -0.05 * flick(0), 0, -0.25 * flick(0));
  set("earR", -0.05 * flick(2.4), 0, 0.25 * flick(2.4));
  for (const [k, o] of [["L", 0], ["R", Math.PI]]) {
    const pf = ph + o, ph2 = ph + o + Math.PI;
    // fore: in the air the forearm angles back and the wrist folds (the paw tucks UP);
    // on the ground the paw cancels every rotation above it and stays flat
    const sf = -Math.sin(pf) * 0.42 * amt, lf = Math.max(0, Math.cos(pf)) * amt, st = Math.max(0, -Math.cos(pf)) * amt;
    const fl = lf * 0.4 - st * 0.08;
    set("foreU" + k, sf, 0, 0);
    set("foreL" + k, fl, 0, 0);
    set("forePaw" + k, -(sf + fl) + lf * 1.0, 0, 0);
    // hind: the knee tucks the shin, the foot folds down and back
    const sh = -Math.sin(ph2) * 0.38 * amt, lh = Math.max(0, Math.cos(ph2)) * amt, sth = Math.max(0, -Math.cos(ph2)) * amt;
    const sn = lh * 0.55 + sth * 0.1;
    set("thigh" + k, sh, 0, 0);
    set("shin" + k, sn, 0, 0);
    set("hindPaw" + k, -(sh + sn) - lh * 0.7, 0, 0);
  }
  // rigid legs rise as they swing: the body sinks with them so planted paws stay down
  const R = B.root;
  if (R && R.userData.y0 !== undefined) R.position.y = R.userData.y0 - 0.027 * amt * Math.pow(Math.sin(ph), 2);
  for (let i = 0; i < 6; i++) {
    const w = Math.sin(t * 1.4 - i * 0.6) * (0.06 + i * 0.02) + Math.sin(ph - i * 0.7) * 0.05 * amt;
    set("tail" + i, (i === 0 ? -0.1 * amt : 0) + Math.sin(t * 0.9 - i * 0.5) * 0.02, w, w * 0.4);
  }
}

// ── SHADOW FOX BEB — the pose (ported from its builder's walkpose.js) ──
// A pup's prancing trot on long legs (more lift, less reach than the
// kitten, and no body sink — measured), a sly head-cock when idle, ear
// flicks, and a wave travelling up the plume. tail0 is never turned: the
// plume's front edge sits ahead of its pivot and would dig into the back.
function foxPupPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const breathe = Math.sin(t * 2.4) * 0.012 * (1 - amt);
  const bob = Math.sin(ph * 2) * 0.03 * amt;
  set("hips", 0.02 * amt + breathe, Math.sin(ph) * 0.05 * amt, Math.sin(ph) * 0.03 * amt);
  set("spine", -bob * 0.5, -Math.sin(ph) * 0.03 * amt, 0);
  set("chest", bob * 0.5 - breathe, -Math.sin(ph) * 0.04 * amt, -Math.sin(ph) * 0.03 * amt);
  set("neck", -bob * 0.6, Math.sin(ph) * 0.05 * amt, 0);
  const look = Math.sin(t * 0.5), cock = Math.sin(t * 0.31 + 1);
  set("head", 0.03 + Math.sin(t * 0.8) * 0.03 * (1 - amt), Math.sign(look) * Math.pow(Math.abs(look), 0.4) * 0.3 * (1 - amt) + Math.sin(ph) * 0.03 * amt, cock * 0.14 * (1 - amt));
  const flick = (o) => Math.max(0, Math.sin(t * 1.7 + o) - 0.9) * 5;
  set("earL", -0.06 * flick(0), 0, -0.3 * flick(0));
  set("earR", -0.06 * flick(2.1), 0, 0.3 * flick(2.1));
  for (const [k, o] of [["L", 0], ["R", Math.PI]]) {
    const pf = ph + o, ph2 = ph + o + Math.PI;
    const sf = -Math.sin(pf) * 0.32 * amt, lf = Math.max(0, Math.cos(pf)) * amt, st = Math.max(0, -Math.cos(pf)) * amt;
    const fl = Math.sqrt(lf) * 0.9 - st * 0.08;
    set("foreU" + k, sf, 0, 0);
    set("foreL" + k, fl, 0, 0);
    set("forePaw" + k, -(sf + fl) + lf * lf * 1.3, 0, 0);
    const sh = -Math.sin(ph2) * 0.38 * amt, lh = Math.max(0, Math.cos(ph2)) * amt;
    const sn = lh * 0.55;
    set("thigh" + k, sh, 0, 0);
    set("shin" + k, sn, 0, 0);
    set("hindPaw" + k, -(sh + sn) - lh * 0.4, 0, 0);
  }
  for (let i = 1; i < 5; i++) {
    const w = Math.sin(t * 1.1 - i * 0.7) * (0.04 + i * 0.03) + Math.sin(ph * 2 - i * 0.6) * 0.05 * amt;
    set("tail" + i, Math.sin(t * 0.8 - i * 0.6) * 0.03 + w * 0.5, w, w * 0.3);
  }
}

// ── INVERTED SERPENT BEB — the pose (ported from its builder's walkpose.js) ──
// No legs: it sways off its coil the way a snake holds itself, the wisps
// curl and uncurl, the head tilts, the tail tip flicks and lifts. Moving,
// the sway rocks with the phase, the body bobs off the coil and the cream
// tail sweeps (it lies outside the heap, so it may).
function serpentBebPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const sway = Math.sin(t * 0.9) * 0.06 * (1 - amt) + Math.sin(ph) * 0.12 * amt;
  set("hips", Math.sin(t * 0.62 + 1.1) * 0.035 + (0.06 + Math.sin(ph * 2) * 0.05) * amt, Math.sin(ph) * 0.1 * amt, sway);
  const R = B.root;
  if (R && R.userData.y0 !== undefined) R.position.y = R.userData.y0 + Math.abs(Math.sin(ph)) * 0.02 * amt;
  set("spine", 0, 0, -sway * 0.5);
  set("chest", -Math.sin(t * 2.0) * 0.012, 0, -sway * 0.4);
  set("neck", 0, 0, -sway * 0.3);
  set("head", Math.sin(t * 0.5) * 0.05, Math.sin(t * 0.37) * 0.15 * (1 - amt * 0.5), Math.sin(t * 0.45 + 0.5) * 0.16 * (1 - amt * 0.5) - sway * 0.2);
  for (const [k, s] of [["L", 1], ["R", -1]]) {
    const c = Math.sin(t * 1.3 + (s > 0 ? 0 : 1.6));
    set("wisp" + k + "0", 0, 0, s * (0.12 * c + 0.08 * amt * Math.sin(ph * 2)));
    set("wisp" + k + "1", 0, 0, s * 0.2 * c);
    set("wisp" + k + "2", 0, 0, s * 0.3 * c);
  }
  for (let i = 0; i < 4; i++) {
    const w = Math.sin(t * 1.2 - i * 0.8) * 0.03 * (i + 1) * (1 - amt) + Math.sin(ph * 1.5 - i * 0.9) * 0.14 * amt;
    const flick = i === 3 ? Math.max(0, Math.sin(t * 0.8) - 0.85) * 3 : 0;
    set("tail" + i, -flick * 0.4 - (i === 3 ? Math.abs(w) * 0.8 : 0), w, 0);
  }
}

// ── SPECTRAL TIGER CHAMPION — the pose (ported from its builder's walkpose.js) ──
// The Beb's curious idle and ear flicks on the fox's long-legged gait (more
// lift, less reach, no body sink — measured), and the smoke streaming on its
// two sway bones, never still.
function tigerChampionPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const breathe = Math.sin(t * 2.1) * 0.012 * (1 - amt);
  const bob = Math.sin(ph * 2) * 0.03 * amt;
  set("hips", 0.02 * amt + breathe, Math.sin(ph) * 0.05 * amt, Math.sin(ph) * 0.03 * amt);
  set("spine", -bob * 0.5, -Math.sin(ph) * 0.03 * amt, 0);
  set("chest", bob * 0.5 - breathe, -Math.sin(ph) * 0.04 * amt, -Math.sin(ph) * 0.03 * amt);
  set("neck", -bob * 0.6, Math.sin(ph) * 0.05 * amt, 0);
  set("head", 0.02 + Math.sin(t * 0.7) * 0.03 * (1 - amt), Math.sin(t * 0.45) * 0.28 * (1 - amt) + Math.sin(ph) * 0.03 * amt, Math.sin(t * 0.33) * 0.08 * (1 - amt));
  const flick = (o) => Math.max(0, Math.sin(t * 1.3 + o) - 0.93) * 6;
  set("earL", -0.05 * flick(0), 0, -0.25 * flick(0));
  set("earR", -0.05 * flick(2.4), 0, 0.25 * flick(2.4));
  for (const [k, o] of [["L", 0], ["R", Math.PI]]) {
    const pf = ph + o, ph2 = ph + o + Math.PI;
    const sf = -Math.sin(pf) * 0.32 * amt, lf = Math.max(0, Math.cos(pf)) * amt, st = Math.max(0, -Math.cos(pf)) * amt;
    const fl = Math.sqrt(lf) * 0.9 - st * 0.08;
    set("foreU" + k, sf, 0, 0);
    set("foreL" + k, fl, 0, 0);
    set("forePaw" + k, -(sf + fl) + lf * lf * 1.3, 0, 0);
    const sh = -Math.sin(ph2) * 0.38 * amt, lh = Math.max(0, Math.cos(ph2)) * amt;
    const sn = lh * 0.55;
    set("thigh" + k, sh, 0, 0);
    set("shin" + k, sn, 0, 0);
    set("hindPaw" + k, -(sh + sn) - lh * 0.4, 0, 0);
  }
  set("smokeL", Math.sin(t * 1.1) * 0.12, 0, -0.1 + Math.sin(t * 0.9 + 1) * 0.15 - amt * 0.1);
  set("smokeR", Math.sin(t * 1.1 + 2) * 0.12, 0, 0.1 - Math.sin(t * 0.9 + 3) * 0.15 + amt * 0.1);
  for (let i = 0; i < 6; i++) {
    const w = Math.sin(t * 1.4 - i * 0.6) * (0.06 + i * 0.02) + Math.sin(ph - i * 0.7) * 0.05 * amt;
    set("tail" + i, (i === 0 ? -0.1 * amt : 0) + Math.sin(t * 0.9 - i * 0.5) * 0.02, w, w * 0.4);
  }
}

// ── SHADOW FOX CHAMPION — the pose (ported from its builder's walkpose.js) ──
// No legs: it glides. The hem twists behind it (a twist, never a tip — tipping
// the hem drives it through the floor), it floats a little, the head is held
// chin-down with eyes closed and turns slowly, an ear turns to listen.
function foxChampionPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const breathe = Math.sin(t * 1.6) * 0.01;
  set("hem", -0.02 * amt, Math.sin(ph * 0.5 - 1.57) * 0.2 * amt, Math.sin(ph * 0.5) * 0.012 * amt + Math.sin(t * 0.7) * 0.006);
  set("hips", 0.03 * amt, Math.sin(ph * 0.5) * 0.04 * amt, 0);
  const R = B.root;
  if (R && R.userData.y0 !== undefined) R.position.y = R.userData.y0 + Math.abs(Math.sin(ph * 0.5)) * 0.02 * amt;
  set("spine", 0.03 * amt + breathe, 0, Math.sin(t * 0.5) * 0.015);
  set("chest", -breathe, 0, 0);
  set("neck", -0.05 * amt, 0, 0);
  set("head", 0.12 + Math.sin(t * 0.4) * 0.03, Math.sin(t * 0.23) * 0.4 * (1 - amt) + Math.sin(ph * 0.5) * 0.26 * amt, Math.sin(t * 0.31) * 0.06);
  const listen = Math.max(0, Math.sin(t * 0.6) - 0.5) * 2;
  set("earL", 0, -0.3 * listen, -0.1 * listen);
  set("earR", 0, 0.3 * Math.max(0, Math.sin(t * 0.6 + 2) - 0.5) * 2, 0);
  set("armL", 0.05 * amt + breathe, 0, 0);
  set("armR", 0.05 * amt + breathe, 0, 0);
}

// ── INVERTED SERPENT CHAMPION — the pose (ported from its builder's walkpose.js) ──
// A cobra's weave: the throat sways, the head holds and tracks, the body's
// loop over its head swings a beat behind (its two sides mirrored); moving,
// the weave grows into a slither and the tail sweeps along the ground.
function serpentChampionPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const sw = Math.sin(t * 0.8) * (1 - amt) * 0.05 + Math.sin(ph) * amt * 0.2;
  const fb = Math.sin(t * 1.6) * (1 - amt) * 0.025 + 0.06 * amt;
  const R = B.root;
  if (R && R.userData.y0 !== undefined) R.position.y = R.userData.y0 + Math.abs(Math.sin(ph)) * 0.015 * amt;
  set("hips", fb * 0.5, Math.sin(ph) * 0.06 * amt, sw);
  set("spine", fb * 0.3, 0, -sw * 0.6);
  set("chest", -fb * 0.3, 0, -sw * 0.8);
  set("neck", -fb * 0.3, 0, sw * 0.3);
  set("head", -fb * 0.2 + Math.sin(t * 0.5) * 0.03, Math.sin(t * 0.37) * 0.3 * (1 - amt), -sw * 0.7);
  const lp = Math.sin(t * 0.8 - 0.8) * 0.04 + Math.sin(ph - 0.8) * 0.12 * amt;
  set("loopL", 0, 0, lp);
  set("loopR", 0, 0, -lp);
  for (let i = 0; i < 4; i++) {
    const w = Math.sin(t * 1.1 - i * 0.8) * 0.03 * (i + 1) * (1 - amt) + Math.sin(ph * 1.5 - i * 0.9) * 0.14 * amt;
    const flick = i === 3 ? Math.max(0, Math.sin(t * 0.9) - 0.85) * 3 : 0;
    set("tail" + i, -flick * 0.4 - (i === 3 ? Math.abs(w) * 0.6 : 0), w, 0);
  }
}

// ── SPECTRAL TIGER LORD (S'aolesh) — the pose (2026-09-23, the Blender sculpt) ──
// A standing, robed sorcerer: the paws held out and turned up, a flame in each,
// rising and falling with his breath; a slow sovereign look-around, ear flicks;
// the tail swaying where it curls out past his left heel; the smoke ribbons
// round his forearms never still. Moving, a stately walk under the coat (the
// coat TWISTS behind him, never tips), the offering arms held.
// Identity rest rotations; +x swings a hanging limb BACK.
function tigerLordPose(B, t, ph, amt) {
  const set = (n, x, y, z) => { const b = B[n]; if (b) b.rotation.set(x || 0, y || 0, z || 0); };
  const breathe = Math.sin(t * 1.4) * 0.015;
  set("hips", 0.02 * amt, Math.sin(ph) * 0.06 * amt, 0);
  set("spine", breathe * 0.5, -Math.sin(ph) * 0.03 * amt, 0);
  set("chest", -breathe, -Math.sin(ph) * 0.03 * amt, 0);
  set("neck", 0.03, Math.sin(t * 0.31) * 0.06 * (1 - amt), 0);
  set("head", 0.04 + Math.sin(t * 0.6) * 0.02, Math.sin(t * 0.37) * 0.28 * (1 - amt) + Math.sin(ph) * 0.04 * amt, Math.sin(t * 0.29) * 0.05);
  const flick = (o) => Math.max(0, Math.sin(t * 1.2 + o) - 0.94) * 6;
  set("earL", -0.06 * flick(0), 0, -0.25 * flick(0));
  set("earR", -0.06 * flick(2.6), 0, 0.25 * flick(2.6));
  // the offering arms: the fire rises and falls
  const lift = Math.sin(t * 1.1) * 0.035;
  set("upperarmL", -0.04 * amt + lift * 0.4, 0, lift);
  set("upperarmR", -0.04 * amt + lift * 0.4, 0, -lift);
  set("handL", -lift * 0.8, 0, 0);
  set("handR", -lift * 0.8, 0, 0);
  for (const [k, o] of [["L", 0], ["R", Math.PI]]) {
    const p = ph + o;
    const th = -Math.sin(p) * 0.34 * amt;                 // thigh: − forward
    const lifted = Math.max(0, Math.cos(p)) * amt;        // the swinging leg's knee bends
    const sn = lifted * 0.55;
    set("thigh" + k, th, 0, 0);
    set("shin" + k, sn, 0, 0);
    set("foot" + k, -(th + sn) * 0.9, 0, 0);              // the planted paw stays flat
  }
  const R = B.root;
  if (R && R.userData.y0 !== undefined) R.position.y = R.userData.y0 - 0.045 * amt * Math.pow(Math.sin(ph), 2);
  set("coat", 0, Math.sin(ph - 0.8) * 0.12 * amt + Math.sin(t * 0.7) * 0.02, 0);
  for (let i = 1; i < 5; i++) {
    const w = Math.sin(t * 1.0 - i * 0.7) * (0.03 + i * 0.03) + Math.sin(ph - i * 0.6) * 0.05 * amt;
    set("tail" + i, 0, w, w * 0.3);
  }
  set("smokeL", Math.sin(t * 1.3) * 0.15, Math.sin(t * 0.9) * 0.25, 0);
  set("smokeR", Math.sin(t * 1.3 + 2) * 0.15, -Math.sin(t * 0.9 + 1) * 0.25, 0);
}

// see-through smoke: a toon ribbon, translucent, never writing depth
const smokeTweak = (opacity) => (o) => {
  if (!/smoke/.test(o.name)) return;
  o.material.transparent = true; o.material.opacity = opacity; o.material.depthWrite = false; o.material.side = THREE.DoubleSide;
};
const both = (...fs) => (o) => fs.forEach((f) => f(o));

// ── THE REGISTRY — "lineage|tier" → the sculpt ────────────────────
// Add a being = add a row (and its glb in public/models/). Nothing else.
const SPECS = {
  "spectral_tiger|beb": {
    url: MODEL_URL("spectral_tiger_beb.glb"), top: 1.12,
    pose: tigerKittenPose, door: QUAD_DOOR,
    noInk: /eyes|noline/,
    glow: { stb_eyes: 0xffffff, stb_whiskers_noline: 0xc8d4dc },
    tweak: rimTweak([0.18, 0.62, 0.66], 0.3),
  },
  "spectral_tiger|champion": {
    url: MODEL_URL("spectral_tiger_champion.glb"), top: 1.054,
    pose: tigerChampionPose, door: QUAD_DOOR,
    noInk: /eyes|noline/,
    glow: { stc_eyes: 0xffffff, stc_whiskers_noline: 0xe8f4f4 },
    tweak: both(rimTweak([0.18, 0.62, 0.66], 0.3), smokeTweak(0.7)),
  },
  "spectral_tiger|lord": {
    // S'aolesh, rebuilt in Blender 2026-09-23 off caP'n's art: a standing robed sorcerer (the
    // 2026-09-16 four-legged sculpt was never wired and is retired). A biped: arms, legs, a tail.
    url: MODEL_URL("spectral_tiger_lord.glb"), top: 1.903,
    pose: tigerLordPose,
    // no legs in the door: a robed caster strikes from the arms and the lean. Thighs swung
    // 0.43 rad by the strike stretched the crotch 9× (stretch test); his walk is his own.
    door: { head: "head", torso: "spine", armL: "upperarmL", armR: "upperarmR", tail: "tail3" },
    noInk: /eyes|noline|flame|smoke/,
    glow: { stl_eyes: 0xffb51b, stl_whiskers_noline: 0xe8f4f4, stl_flame: 0xe8fff4 },
    tweak: both(rimTweak([0.18, 0.62, 0.66], 0.3, /body|head|coat/), smokeTweak(0.55)),
  },
  "shadow_fox|champion": {
    url: MODEL_URL("shadow_fox_champion.glb"), top: 1.457,
    pose: foxChampionPose,
    // a robed figure: the arm slots are the robe's shoulders; no legs, no tail
    door: { head: "head", torso: "spine", armL: "armL", armR: "armR" },
    noInk: /eyes|noline/,
    glow: { sfc_gem: 0xffffff, sfc_gold_noline: 0xffffff },
    tweak: rimTweak([0.35, 0.29, 0.48], 0.25, /robe|head/),
  },
  "inverted_serpent|champion": {
    url: MODEL_URL("inverted_serpent_champion.glb"), top: 1.178,
    pose: serpentChampionPose,
    // no legs: the arm slots are the two falls of its loop; the tail's first bone is pinned
    door: { head: "head", torso: "spine", armL: "loopL", armR: "loopR", tail: "tail1" },
    noInk: /eyes|noline/,
    glow: { isc_fangs_noline: 0xffffff },
    tweak: rimTweak([0.5, 0.68, 0.54], 0.3, /skin|loop|coil/),
  },
  "shadow_fox|beb": {
    url: MODEL_URL("shadow_fox_beb.glb"), top: 1.05,
    pose: foxPupPose,
    // the plume's strike sway goes on tail1 (tail0 would dig into the back)
    door: { ...QUAD_DOOR, tail: "tail1" },
    noInk: /eyes|noline/,
    glow: { sfb_eyes: 0xffffff },
    tweak: rimTweak([0.35, 0.29, 0.48], 0.25),
  },
  "inverted_serpent|beb": {
    url: MODEL_URL("inverted_serpent_beb.glb"), top: 0.81,
    pose: serpentBebPose,
    // no legs; the wisps are its arms; the tail's first bone is pinned under the heap
    door: { head: "head", torso: "spine", armL: "wispL0", armR: "wispR0", tail: "tail1" },
    noInk: /eyes|noline/,
    glow: { isb_wisps_noline: 0xffffff },
    tweak: rimTweak([0.37, 0.66, 0.67], 0.5, /_skin$/),
  },
};

// the painted kit body, reporting itself so the host can re-fit
function PaintedBeing({ lineage, tier, pal, onSkin }) {
  useLayoutEffect(() => { if (onSkin) onSkin("p", `${lineage}|${tier}`); }, [onSkin, lineage, tier]);
  return <BeingMesh beingId={lineage} tier={tier} pal={pal} />;
}

const BODIES = {};
function sculptFor(key) {
  if (BODIES[key] !== undefined) return BODIES[key];
  const spec = SPECS[key];
  if (!spec) return (BODIES[key] = null);
  const { door, pose, ...rest } = spec;
  const Sculpted = makeSculptedBody({
    ...rest,
    pose: (B, t, ph, amt) => pose(B, t, ph, amt),
    // on mount (a layout effect): open the rig door, then tell the host this body stands
    onMount: (S, ctl) => {
      Object.entries(door || {}).forEach(([slot, bone]) => { if (S.B[bone]) S.B[bone].name = "rig_" + slot; });
      if (ctl && ctl.onSkin) ctl.onSkin("s", key);
    },
  });
  return (BODIES[key] = failSafe(Sculpted, PaintedBeing));
}

export const hasSculpt = (lineage, tier) => !!SPECS[`${lineage}|${tier}`];

// The door. Same props on both bodies; `ctl` is a stable object the host
// may write `speed` into.
export function BeingBody({ lineage, tier, pal, onSkin, ctl }) {
  const own = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  const c = ctl || own;
  c.onSkin = onSkin;
  const Body = sculptFor(`${lineage}|${tier}`);
  if (!Body) return <PaintedBeing lineage={lineage} tier={tier} pal={pal} onSkin={onSkin} />;
  return <Body ctl={c} lineage={lineage} tier={tier} pal={pal} onSkin={onSkin} />;
}

export default BeingBody;
